export enum Permission {
  // Organization Permissions
  LIST_ORGANIZATIONS = 'organization:listOrganizations',
  VIEW_ORGANIZATION = 'organization:viewOrganization',
  UPDATE_ORGANIZATION = 'organization:updateOrganization',
  DELETE_ORGANIZATION = 'organization:deleteOrganization',

  // Project Permissions
  CREATE_PROJECT = 'project:createProject',
  LIST_PROJECTS = 'project:listProjects',
  VIEW_PROJECT = 'project:viewProject',
  UPDATE_PROJECT = 'project:updateProject',
  DELETE_PROJECT = 'project:deleteProject',

  // Task Permissions
  CREATE_TASK = 'task:createTask',
  LIST_TASKS = 'task:listTasks',
  VIEW_TASK = 'task:viewTask',
  UPDATE_TASK = 'task:updateTask',
  DELETE_TASK = 'task:deleteTask',

  // Permission Permissions
  CREATE_ORG_GRANT = 'permission:createOrgGrant',
  CREATE_PROJECT_GRANT = 'permission:createProjectGrant',
  LIST_GRANTS = 'permission:listGrants',
  VIEW_GRANT = 'permission:viewGrant',
  VIEW_RESOURCES_WITH_PERMISSION = 'permission:viewResourcesWithPermission',
  DELETE_ORG_GRANT = 'permission:deleteOrgGrant',
  DELETE_PROJECT_GRANT = 'permission:deleteProjectGrant',
  LIST_USER_GRANTS = 'permission:listUserGrants',
  VIEW_USER_GRANT = 'permission:viewUserGrant',

  // Asset Permissions
  CREATE_ASSET = 'asset:createAsset',
  LIST_ASSETS = 'asset:listAssets',
  VIEW_ASSET = 'asset:viewAsset',
  UPDATE_ASSET = 'asset:updateAsset',
  DELETE_ASSET = 'asset:deleteAsset',

  // Asset Set Permissions
  CREATE_ASSET_SET = 'asset:createAssetSet',
  LIST_ASSET_SETS = 'asset:listAssetSets',
  VIEW_ASSET_SET = 'asset:viewAssetSet',
  UPDATE_ASSET_SET = 'asset:updateAssetSet',
  DELETE_ASSET_SET = 'asset:deleteAssetSet',

  // Group Permissions
  CREATE_GROUP = 'group:createGroup',
  LIST_GROUPS = 'group:listGroups',
  VIEW_GROUP = 'group:viewGroup',
  UPDATE_GROUP = 'group:updateGroup',
  DELETE_GROUP = 'group:deleteGroup',

  // Group User Permissions
  CREATE_GROUP_USER = 'groupUser:createGroupUser',
  LIST_GROUP_USERS = 'groupUser:listGroupUsers',
  VIEW_GROUP_USER = 'groupUser:viewGroupUser',
  DELETE_GROUP_USER = 'groupUser:deleteGroupUser',

  // Organisation User Permissions
  CREATE_ORGANISATION_USER = 'organisationUser:createOrganisationUser',
  LIST_ORGANISATION_USERS = 'organisationUser:listOrganisationUsers',
  VIEW_ORGANISATION_USER = 'organisationUser:viewOrganisationUser',
  UPDATE_ORGANISATION_USER = 'organisationUser:updateOrganisationUser',
  DELETE_ORGANISATION_USER = 'organisationUser:deleteOrganisationUser',

  // Tile Permissions (for external token access)
  VIEW_TILES = 'tiles:viewTiles',

  // Geometry Permissions
  CREATE_GEOMETRY = 'geometry:createGeometry',
  LIST_GEOMETRIES = 'geometry:listGeometries',
  VIEW_GEOMETRY = 'geometry:viewGeometry',
  UPDATE_GEOMETRY = 'geometry:updateGeometry',
  DELETE_GEOMETRY = 'geometry:deleteGeometry',
}

/* ---------------------- Token Permissions ---------------------- */

/**
 * Permissions that can be granted to external tokens.
 * This is a subset of the full Permission set.
 * Tokens can only be created with these permissions as scopes.
 */
export const TOKEN_PERMISSIONS = new Set<Permission>([
  Permission.VIEW_TILES,
  Permission.VIEW_ASSET,
  Permission.LIST_ASSETS,
  Permission.VIEW_ASSET_SET,
  Permission.VIEW_TASK,
  Permission.LIST_TASKS,
])

/**
 * Mapping from Permission to the token permission(s) that grant it.
 * Some permissions can be granted by multiple token scopes.
 * If a permission is not in this map, it cannot be accessed via token.
 */
export const PERMISSION_TO_TOKEN_PERMISSIONS: Partial<
  Record<Permission, Permission[]>
> = {
  // VIEW_TILES scope grants VIEW_ASSET permission (needed for tile serving)
  [Permission.VIEW_ASSET]: [Permission.VIEW_TILES, Permission.VIEW_ASSET],
  [Permission.LIST_ASSETS]: [Permission.LIST_ASSETS],
  [Permission.VIEW_TASK]: [Permission.VIEW_TASK],
  [Permission.LIST_TASKS]: [Permission.LIST_TASKS],
  // VIEW_ASSET_SET is needed for the getAssetSet call in projectId validation
  [Permission.VIEW_ASSET_SET]: [Permission.VIEW_TILES, Permission.VIEW_ASSET],
}

/**
 * Check if a permission is allowed by the given token scopes.
 * Returns true if no token scopes are provided (regular auth, not token-based).
 * Returns true if at least one of the token's scopes grants the permission.
 * Returns false if the permission has no scope requirements (not available via token).
 */
export const isPermissionAllowedByTokenScopes = (
  permission: Permission,
  tokenScopes?: string[],
): boolean => {
  // No token scopes means this is regular auth (Cognito), not token-based
  if (!tokenScopes || tokenScopes.length === 0) {
    return true
  }

  // Get the scopes that can grant this permission
  const allowedScopes = PERMISSION_TO_TOKEN_PERMISSIONS[permission]

  // If permission has no scope requirements, it's not available via token
  if (!allowedScopes) {
    return false
  }

  // Check if any of the token's scopes grant this permission
  return tokenScopes.some((scope) =>
    allowedScopes.includes(scope as Permission),
  )
}
